// src/components/dashboards/AnalysisReportView.tsx
// Multi-finding report renderer for Mode B autonomous analysis.
// Displays narrative, metric highlights, and per-finding charts.

import React, { useMemo } from "react";
import {
  alpha,
  Box,
  Chip,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TimerIcon from "@mui/icons-material/Timer";
import AgenticChartRenderer from "./charts/agentic/AgenticChartRenderer";
import type { NLQueryResult, VisualizationMeta } from "hooks/useNLQuery";
import { useTranslation } from "../../i18n";

export interface AnalysisFinding {
  title: string;
  description: string;
  significance: "high" | "medium" | "low";
  strategy: string;
  visualization: VisualizationMeta;
  rows: Record<string, unknown>[];
  columns: string[];
}

export interface AnalysisReportData {
  type: "analysis_report";
  title: string;
  narrative: string;
  findings: AnalysisFinding[];
  metadata: {
    queries_executed: number;
    llm_calls: number;
    elapsed_seconds: number;
    total_rows_analyzed: number;
    budget_remaining: {
      queries: number;
      llm_calls: number;
    };
    partial: boolean;
  };
  session_id?: string;
  suggested_followups?: string[];
}

interface Props {
  report: AnalysisReportData;
}

type SignificanceLevel = "high" | "medium" | "low";

const AnalysisReportView: React.FC<Props> = ({ report }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const textPrimary =
    theme.custom?.contentText?.primary || theme.palette.text.primary;
  const textSecondary =
    theme.custom?.contentText?.secondary || theme.palette.text.secondary;

  const sortedFindings = useMemo(() => {
    const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return [...report.findings].sort(
      (a, b) => (order[a.significance] ?? 1) - (order[b.significance] ?? 1)
    );
  }, [report.findings]);

  const glassCard = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    background: alpha(theme.palette.background.paper, 0.55),
    backdropFilter: "blur(12px)",
    p: 2.5,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header + Narrative */}
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <ScienceIcon
            sx={{ color: theme.palette.primary.main, fontSize: 22 }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ color: textPrimary, fontSize: "1rem" }}
          >
            {report.title}
          </Typography>
          {report.metadata.partial && (
            <Chip
              label={t("analysis.partial", "Partial")}
              size="small"
              sx={{
                ml: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.15),
                color: theme.palette.warning.main,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: textSecondary,
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
          }}
        >
          {report.narrative}
        </Typography>

        {/* Metadata chips */}
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <MetaChip
            icon={<QueryStatsIcon sx={{ fontSize: 14 }} />}
            label={t(
              "analysis.queries_executed",
              `${report.metadata.queries_executed} queries`
            )}
          />
          <MetaChip
            icon={<ScienceIcon sx={{ fontSize: 14 }} />}
            label={t(
              "analysis.llm_calls",
              `${report.metadata.llm_calls} LLM calls`
            )}
          />
          <MetaChip
            icon={<TimerIcon sx={{ fontSize: 14 }} />}
            label={t(
              "analysis.elapsed",
              `${report.metadata.elapsed_seconds}s`
            )}
          />
          <MetaChip
            label={t(
              "analysis.rows_analyzed",
              `${report.metadata.total_rows_analyzed.toLocaleString()} rows`
            )}
          />
        </Box>
      </Box>

      {/* Findings */}
      {sortedFindings.map((finding, idx) => (
        <FindingCard
          key={idx}
          finding={finding}
          index={idx}
          glassCard={glassCard}
        />
      ))}
    </Box>
  );
};

const MetaChip: React.FC<{
  label: string;
  icon?: React.ReactNode;
}> = ({ label, icon }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.3,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color:
          theme.custom?.contentText?.secondary ||
          theme.palette.text.secondary,
        fontSize: "0.7rem",
        fontWeight: 500,
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

const FindingCard: React.FC<{
  finding: AnalysisFinding;
  index: number;
  glassCard: Record<string, unknown>;
}> = ({ finding, index, glassCard }) => {
  const theme = useTheme();
  const textPrimary =
    theme.custom?.contentText?.primary || theme.palette.text.primary;
  const textSecondary =
    theme.custom?.contentText?.secondary || theme.palette.text.secondary;

  const significanceColorMap: Record<SignificanceLevel, string> = {
    high: theme.palette.error.main,
    medium: theme.palette.warning.main,
    low: theme.palette.success.main,
  };
  const sigColor = significanceColorMap[finding.significance as SignificanceLevel] || theme.palette.warning.main;

  const chartResult: NLQueryResult = {
    question: "",
    explanation: "",
    columns: finding.columns,
    rows: finding.rows,
    row_count: finding.rows.length,
    tables_used: [],
    visualization: finding.visualization,
  };

  return (
    <Box sx={glassCard}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: sigColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: textPrimary, fontSize: "0.85rem" }}
        >
          {finding.title}
        </Typography>
        <Chip
          label={finding.significance}
          size="small"
          sx={{
            ml: "auto",
            bgcolor: alpha(sigColor, 0.12),
            color: sigColor,
            fontWeight: 600,
            fontSize: "0.65rem",
            textTransform: "capitalize",
            height: 20,
          }}
        />
      </Box>

      {finding.description && (
        <Typography
          variant="body2"
          sx={{ color: textSecondary, mb: 1.5, fontSize: "0.8rem" }}
        >
          {finding.description}
        </Typography>
      )}

      {finding.rows.length > 0 && finding.visualization.type !== "table" && (
        <Box sx={{ mt: 1 }}>
          <AgenticChartRenderer result={chartResult} />
        </Box>
      )}

      {finding.rows.length > 0 && finding.visualization.type === "table" && (
        <Box
          sx={{
            mt: 1,
            maxHeight: 200,
            overflow: "auto",
            fontSize: "0.75rem",
            "& table": {
              width: "100%",
              borderCollapse: "collapse",
            },
            "& th, & td": {
              p: 0.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              textAlign: "left",
              color: textSecondary,
            },
            "& th": {
              fontWeight: 600,
              color: textPrimary,
            },
          }}
        >
          <table>
            <thead>
              <tr>
                {finding.columns.slice(0, 8).map((col) => (
                  <th key={col}>{col.replace(/_/g, " ")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {finding.rows.slice(0, 20).map((row, ri) => (
                <tr key={ri}>
                  {finding.columns.slice(0, 8).map((col) => (
                    <td key={col}>{formatCellValue(row[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
}

export default AnalysisReportView;
